<script setup lang="ts">
import type { HTMLAttributes } from "vue"
import { useRouter } from "vue-router"
import { ThemeButton, ThemeImg } from "~/components/theme"
import { useLocation } from "~/composables/locations"
import { scrollToAndFocus } from "~/composables/scroll"
import { cn } from "~/lib/utils"

const { class: cl } = defineProps<{
    class?: HTMLAttributes["class"]
}>()

const { location, menuPath } = useLocation()
const cateringMenus = computed(() => location.value?.menus.catering)
const router = useRouter()

async function handleScrollToMenu(event: Event) {
    event.preventDefault()
    
    await router.push(menuPath("catering"))
    
    scrollToAndFocus("menus")
}
</script>

<template>
    <div
        v-if="location"
        id="catering"
        tabindex="-1"
        :class="cn('space-y-10 md:space-y-12 px-4 sm:px-6', cl)"
    >
        <header class="space-y-6 text-center">
            <h2 class="text-2xl sm:text-3xl lg:text-4xl font-headline text-balance max-w-full overflow-hidden">
                {{ location?.pageContent && location.pageContent.catering?.title }}
            </h2>
        </header>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            <div 
                v-if="location?.pageContent && location.pageContent.catering?.image"
                class="hidden lg:flex lg:flex-1 min-w-0"
            >
                <ThemeImg
                    :src="location.pageContent.catering.image"
                    :alt="location.pageContent.catering.imageAlt || ''"
                    class="size-full object-cover"
                />
            </div>
            <div 
                class="flex-1 flex flex-col gap-6 justify-between min-w-0"
            >
                <ThemeImg
                    v-if="location?.pageContent && location.pageContent.catering?.image2"
                    :src="location.pageContent.catering.image2"
                    :alt="location.pageContent.catering.image2Alt || ''"
                    class="w-full aspect-[4/3] lg:max-h-60 object-cover"
                />
                <div class="flex flex-col justify-around gap-6 py-6 lg:p-8 bg-foreground/10 text-foreground border-l-8 border-background/50 min-w-0">
                    <p
                        class="max-w-lg mx-auto space-y-4 text-center text-balance break-words"
                        v-html="(location?.pageContent && location.pageContent.catering?.content) || ''"
                    />
                    <div class="flex flex-col items-stretch sm:items-center justify-center gap-4 mx-auto">
                        <ThemeButton 
                            size="lg"
                            variant="outline"
                            tabindex="0"
                            class="border-foreground/20 w-full sm:w-auto text-balance"
                            aria-label="View Catering Menus"
                            @click="handleScrollToMenu"
                        >
                            {{ cateringMenus && cateringMenus.length > 1 ? "View Catering Menus" : "View Catering Menu" }}
                        </ThemeButton>
                        <ThemeButton
                            size="lg"
                            class="w-full sm:w-auto"
                            as-child
                        >
                            <a
                                :href="location?.external?.url?.onlineOrderingCatering"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="block w-full sm:w-auto"
                                tabindex="0"
                                aria-label="Order Catering Delivery via FamilyMeal"
                            >
                                Order Now
                            </a>
                        </ThemeButton>
                    </div>
                </div> 
            </div>
        </div>
    </div>
</template>